import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Flex,
  Spin,
  Upload,
  message,
  Alert,
  Select,
} from "antd";
import { StandaloneSearchBox } from "@react-google-maps/api";
import validations from "../../utils/validations";
import axios from "axios";
import { LoadingOutlined } from "@ant-design/icons";

const { Option } = Select;

const COMPANY_ROLES = [
  { value: 1, label: "Грузовладелец" },
  { value: 2, label: "Перевозчик" },
  { value: 3, label: "Экспедитор" },
  { value: 4, label: "Экспедитор-перевозчик" },
];

const COMPANY_TYPES = [
  { value: 1, label: "ОсОО" },
  { value: 2, label: "ИП" },
  { value: 3, label: "ОАО" },
];

const EMPLOYEES_OPTIONS = [
  { value: "1-15", label: "1-15" },
  { value: "16-50", label: "16-50" },
  { value: "51-150", label: "51-150" },
  { value: "151-500", label: "151-500" },
  { value: "501-1500", label: "501-1500" },
  { value: "1501-5000", label: "1501-5000" },
];

const ORDERS_MONTH_OPTIONS = [
  { value: "1-50", label: "1-50" },
  { value: "51-100", label: "51-100" },
  { value: "101-500", label: "101-500" },
  { value: "501-1000", label: "501-1000" },
  { value: "1001-5000", label: "1001-5000" },
  { value: "5001-100000", label: "5001-100000" },
];

const VEHICLES_OPTIONS = [
  { value: "1-5", label: "1-5" },
  { value: "6-15", label: "6-15" },
  { value: "16-50", label: "16-50" },
  { value: "51-100", label: "51-100" },
  { value: "101-500", label: "101-500" },
  { value: "501-1000", label: "501-1000" },
];

// Function to convert file to base64
const getBase64 = (file, callback) => {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result));
  reader.readAsDataURL(file);
};

const CompanyProfile = ({ showModal, closeModal, profile, setProfile }) => {
  const [loading, setLoading] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("KG");
  const [phoneInputValue, setPhoneInputValue] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);

  const handleRoleChange = (value) => {
    setSelectedRole(value);
    // Clear validation errors for fields that might be hidden
    form.setFields([
      {
        name: "orders_month",
        errors: [],
      },
      {
        name: "vehicles_numbers",
        errors: [],
      },
    ]);
  };

  const shouldShowOrdersMonth = () => {
    return selectedRole === 1 || selectedRole === 3 || selectedRole === 4;
  };

  const shouldShowVehiclesNumbers = () => {
    return selectedRole === 2 || selectedRole === 4;
  };

  const countries = [
    { code: "KG", flag: "🇰🇬", prefix: "+996", format: "xxx xx-xx-xx" },
    { code: "KZ", flag: "🇰🇿", prefix: "+7", format: "xxx xxx-xx-xx" },
    { code: "UZ", flag: "🇺🇿", prefix: "+998", format: "xx xxx-xx-xx" },
    { code: "RU", flag: "🇷🇺", prefix: "+7", format: "xxx xxx-xx-xx" },
    { code: "BY", flag: "🇧🇾", prefix: "+375", format: "xx xxx-xx-xx" },
  ];

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
  };

  const handlePhoneInputChange = (e) => {
    setPhoneInputValue(e.target.value);
  };

  // States for certificate and license images
  const [certificateUrl, setCertificateUrl] = useState();
  const [licenseUrl, setLicenseUrl] = useState();
  const [base64Certificate, setBase64Certificate] = useState("");
  const [base64License, setBase64License] = useState("");

  const [imageUrl, setImageUrl] = useState(null);
  const [base64Image, setBase64Image] = useState(null);

  const [form] = Form.useForm();

  useEffect(() => {
    if (profile && profile.company_profile) {
      form.setFieldsValue(profile.company_profile);
      // Initialize selectedRole from profile data
      if (profile.company_profile.role) {
        setSelectedRole(profile.company_profile.role);
        // Initialize conditional fields based on role
        const role = profile.company_profile.role;

        // For roles that need orders_month (1, 3, 4), ensure it's in form values
        if (
          (role === 1 || role === 3 || role === 4) &&
          profile.company_profile.orders_month
        ) {
          form.setFieldsValue({
            orders_month: profile.company_profile.orders_month,
          });
        }

        // For roles that need vehicles_numbers (2, 4), ensure it's in form values
        if (
          (role === 2 || role === 4) &&
          profile.company_profile.vehicles_numbers
        ) {
          form.setFieldsValue({
            vehicles_numbers: profile.company_profile.vehicles_numbers,
          });
        }
      }

      // Extract country code from phone number and set phone input value
      if (profile.company_profile.phone) {
        const phoneNumber = profile.company_profile.phone.toString();
        // Determine country based on phone number prefix
        if (phoneNumber.startsWith("996")) {
          setSelectedCountry("KG");
          setPhoneInputValue(phoneNumber.substring(3));
        } else if (phoneNumber.startsWith("7")) {
          setSelectedCountry("KZ");
          setPhoneInputValue(phoneNumber.substring(1));
        } else if (phoneNumber.startsWith("998")) {
          setSelectedCountry("UZ");
          setPhoneInputValue(phoneNumber.substring(3));
        } else if (phoneNumber.startsWith("375")) {
          setSelectedCountry("BY");
          setPhoneInputValue(phoneNumber.substring(3));
        } else {
          setSelectedCountry("KG");
          setPhoneInputValue(phoneNumber);
        }
      }

      // Set certificate and license images if they exist
      if (profile.company_profile.certificate) {
        // Check if it's a media URL or base64 string
        if (profile.company_profile.certificate.match(/\/media\//) !== null) {
          setCertificateUrl(profile.company_profile.certificate);
        } else {
          setCertificateUrl(
            `data:image/jpeg;base64,${profile.company_profile.certificate}`,
          );
        }
      }

      if (
        profile.company_profile.profile_images &&
        profile.company_profile.profile_images.match(/\/media\//) !== null
      ) {
        setImageUrl(profile.company_profile.profile_images);
      }

      if (profile.company_profile.license) {
        // Check if it's a media URL or base64 string
        if (profile.company_profile.license.match(/\/media\//) !== null) {
          setLicenseUrl(profile.company_profile.license);
        } else {
          setLicenseUrl(
            `data:image/jpeg;base64,${profile.company_profile.license}`,
          );
        }
      }
    }
  }, [profile, form]);

  // Close modal on route change
  useEffect(() => {
    if (!showModal) return;

    const currentPathname = window.location.pathname;

    const checkRouteChange = () => {
      if (window.location.pathname !== currentPathname) {
        closeModal();
      }
    };

    // Check for route changes periodically
    const intervalId = setInterval(checkRouteChange, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, [showModal, closeModal]);

  const handleCancel = () => {
    closeModal();
  };

  const onFinishFailed = () => {
    console.log("Failed");
  };

  // Validation function for uploaded images
  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("Можно загружать только JPEG/PNG файлы");
      return false;
    }

    const isLt3M = file.size / 1024 / 1024 < 3;
    if (!isLt3M) {
      message.error("Файл должен быть меньше 3 мегабайт");
      return false;
    }

    return true;
  };

  const handleChange = (info) => {
    if (info.file.status === "uploading") {
      return;
    }

    if (info.file.status === "done") {
      // Get the base64 from the file reader
      getBase64(info.file.originFileObj, (imageUrl) => {
        setImageUrl(imageUrl);
        setBase64Image(imageUrl);

        // Update form values with the base64 image
        const formValues = form.getFieldsValue();
        form.setFieldsValue({
          ...formValues,
          profile_images: imageUrl.split(",")[1],
        });
      });
    }
  };

  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const uploadButton = (
    <div>
      <div style={{ marginTop: 8 }} className="upload-profile-pic-empty">
        <span className="upload-profile-pic-empty__plus">
          <svg
            width="24"
            height="25"
            viewBox="0 0 24 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="11" y="3" width="2" height="18" rx="1" fill="black" />
            <rect
              x="21"
              y="11"
              width="2"
              height="18"
              rx="1"
              transform="rotate(90 21 11)"
              fill="black"
            />
          </svg>
        </span>
      </div>
    </div>
  );

  // Handle certificate upload
  const handleCertificateChange = (info) => {
    if (info.file.status === "uploading") {
      return;
    }

    if (info.file.status === "done") {
      getBase64(info.file.originFileObj, (imageUrl) => {
        setCertificateUrl(imageUrl);
        setBase64Certificate(imageUrl);

        // Update form values with the base64 image
        const formValues = form.getFieldsValue();
        form.setFieldsValue({
          ...formValues,
          certificate: imageUrl.split(",")[1],
        });
      });
    }
  };

  // Handle license upload
  const handleLicenseChange = (info) => {
    if (info.file.status === "uploading") {
      return;
    }

    if (info.file.status === "done") {
      getBase64(info.file.originFileObj, (imageUrl) => {
        setLicenseUrl(imageUrl);
        setBase64License(imageUrl);

        // Update form values with the base64 image
        const formValues = form.getFieldsValue();
        form.setFieldsValue({
          ...formValues,
          license: imageUrl.split(",")[1],
        });
      });
    }
  };

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      await form.validateFields();

      // Get country prefix without + symbol
      const country = countries.find((c) => c.code === selectedCountry);
      const countryPrefix = country.prefix.replace("+", "");

      // Combine country code with phone number (remove any existing country code)
      const phoneDigits = phoneInputValue.replace(/\D/g, "");
      const phoneWithCountryCode = countryPrefix + phoneDigits;

      // Create payload without phone field from form values
      const { phone, ...formValuesWithoutPhone } = values;
      const payload = {
        ...formValuesWithoutPhone,
        phone: phoneWithCountryCode,
      };

      const url = profile ? `auth/profile/${profile.id}/` : "auth/profile/";

      // Remove conditional fields that are not relevant to the selected role
      if (!shouldShowOrdersMonth()) {
        delete payload.orders_month;
      }
      if (!shouldShowVehiclesNumbers()) {
        delete payload.vehicles_numbers;
      }

      if (values.certificate && values.certificate.includes("/media/")) {
        delete payload.certificate;
      }

      if (values.license && values.license.includes("/media/")) {
        delete payload.license;
      }
      if (values.profile_images && values.profile_images.includes("/media/")) {
        delete payload.profile_images;
      }

      const { status, data } = await axios({
        url: `${process.env.REACT_APP_API_URL}/${url}`,
        method: profile ? "PUT" : "POST",
        headers: {
          Authorization: `JWT ${window.localStorage.getItem("access")}`,
        },
        data: {
          company_profile: payload,
        },
      });
      if (status === 201 || status === 200) {
        closeModal();
        setProfile(data);
      }
      window.location.reload();
    } catch (error) {
      console.log("Validation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlacesChanged = () => {
    if (autocomplete) {
      const places = autocomplete.getPlaces();
      if (places && places.length > 0) {
        const selectedPlace = places[0];
        // Update state instead of directly setting form values
        form.setFieldsValue({ address: selectedPlace.formatted_address });
      }
    }
  };

  console.log("showModal", showModal);

  return (
    <Modal
      title="Профиль компании"
      closable={{ "aria-label": "Custom Close Button" }}
      classNames={{ mask: "company-profile-modal-mask" }}
      maskClosable={false}
      open={showModal}
      footer={null}
      onCancel={handleCancel}
      style={{ maxWidth: 620 }}
    >
      {loading && (
        <div className="transport-search-full-screen">
          <Flex align="center" gap="middle">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            />
          </Flex>
        </div>
      )}
      <div className="profile">
        <Form
          form={form}
          name="profile"
          layout="vertical"
          className="profile-form-form"
          onFinish={onSubmit}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <Form.Item
              name="profile_images"
              label=" "
              className="avatar-uploader-form"
              valuePropName="profile_images"
            >
              <Upload
                name="profile_images"
                listType="picture-circle"
                className="avatar-uploader"
                showUploadList={false}
                accept=".jpg,.jpeg,.png"
                beforeUpload={beforeUpload}
                onChange={handleChange}
                customRequest={customRequest}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="profile_image"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
            </Form.Item>
          </div>
          <Flex style={{ justifyContent: "space-between" }}>
            <Form.Item
              style={{ width: "48%" }}
              name="type"
              label="Тип компании"
              className="non-floating-form-item"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, выберите тип компании",
                },
              ]}
            >
              <Select placeholder="" size="large">
                {COMPANY_TYPES.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="role"
              label="Роль компании"
              className="non-floating-form-item"
              style={{ width: "48%" }}
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, выберите тип компании",
                },
              ]}
            >
              <Select
                placeholder=""
                size="large"
                onChange={(value) => {
                  handleRoleChange(value);
                  form.setFieldsValue({ role: value });
                }}
                value={selectedRole}
              >
                {COMPANY_ROLES.map((role) => (
                  <Option key={role.value} value={role.value}>
                    {role.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Flex>
          <Form.Item
            name="company_name"
            label="Название компании"
            className="non-floating-form-item"
            rules={[validations.required]}
          >
            <Input placeholder="" />
          </Form.Item>
          <Flex gap="middle">
            <Form.Item
              name="country"
              label="Страна"
              className="non-floating-form-item"
              rules={[
                { required: true, message: "Пожалуйста, выберите страну" },
              ]}
              style={{ flex: 1 }}
            >
              <Select placeholder="" size="large">
                <Option value="Кыргызстан">Кыргызстан</Option>
                <Option value="Казахстан">Казахстан</Option>
                <Option value="Узбекистан">Узбекистан</Option>
                <Option value="Россия">Россия</Option>
                <Option value="Белоруссия">Белоруссия</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="city"
              label="Город"
              className="non-floating-form-item"
              rules={[{ required: true, message: "Пожалуйста, введите город" }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="" />
            </Form.Item>
          </Flex>
          <Form.Item
            label="Телефон компании"
            className="non-floating-form-item profile-phone-form-item"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите телефон компании",
              },
            ]}
          >
            <Input
              placeholder="Введите номер телефона"
              value={phoneInputValue}
              onChange={handlePhoneInputChange}
              size="large"
              addonBefore={
                <Select
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  style={{ width: 120 }}
                >
                  {countries.map((country) => (
                    <Option key={country.code} value={country.code}>
                      {country.flag} {country.prefix}
                    </Option>
                  ))}
                </Select>
              }
            />
          </Form.Item>
          <Flex gap="middle">
            <Form.Item
              name="OKPO"
              label="ОКПО"
              className="non-floating-form-item"
              rules={[{ required: true, message: "Пожалуйста, введите ОКПО" }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="" />
            </Form.Item>
            <Form.Item
              name="INN"
              label="ИНН"
              className="non-floating-form-item"
              rules={[{ required: true, message: "Пожалуйста, введите ИНН" }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="" />
            </Form.Item>
          </Flex>
          <StandaloneSearchBox
            onLoad={(ref) => setAutocomplete(ref)}
            onPlacesChanged={handlePlacesChanged}
          >
            <Form.Item
              label="Адрес"
              name="address"
              className="non-floating-form-item"
              rules={[{ required: true, message: "Пожалуйста, введите адрес" }]}
            >
              <Input placeholder="" size="large" />
            </Form.Item>
          </StandaloneSearchBox>

          <Flex gap="middle">
            <Form.Item
              name="website"
              label="Веб-сайт"
              className="non-floating-form-item"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите веб-сайт компании",
                },
              ]}
              style={{ flex: 1 }}
            >
              <Input placeholder="" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              className="non-floating-form-item"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите email компании",
                },
              ]}
              style={{ flex: 1 }}
            >
              <Input placeholder="" />
            </Form.Item>
          </Flex>
          <Flex gap="middle">
            <Form.Item
              name="employers_number"
              label="Количество сотрудников"
              className="non-floating-form-item"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, выберите количество сотрудников",
                },
              ]}
              style={{ flex: 1 }}
            >
              <Select placeholder="" size="large">
                {EMPLOYEES_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Flex>
          <Flex gap="middle">
            {shouldShowOrdersMonth() && (
              <Form.Item
                name="orders_month"
                label="Перевозки в месяц"
                className="non-floating-form-item"
                rules={[
                  {
                    required: selectedRole === 1 || selectedRole === 3,
                    message: "Пожалуйста, выберите количество заказов в месяц",
                  },
                ]}
                style={{ flex: selectedRole === 4 ? 1 : "48%" }}
              >
                <Select placeholder="" size="large">
                  {ORDERS_MONTH_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {shouldShowVehiclesNumbers() && (
              <Form.Item
                name="vehicles_numbers"
                label="Размер автопарка"
                className="non-floating-form-item"
                rules={[
                  {
                    required: selectedRole === 2,
                    message:
                      "Пожалуйста, выберите количество транспортных средств",
                  },
                ]}
                style={{ flex: selectedRole === 4 ? 1 : "48%" }}
              >
                <Select placeholder="" size="large">
                  {VEHICLES_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Flex>

          <div className="uploader-controls">
            <Form.Item
              name="certificate"
              label="Свидетельство о регистрации"
              className="non-floating-form-item"
              valuePropName="certificate"
            >
              <Upload
                name="certificate"
                listType="picture-card"
                className="certificate-uploader"
                showUploadList={false}
                beforeUpload={beforeUpload}
                onChange={handleCertificateChange}
                customRequest={({ onSuccess }) =>
                  setTimeout(() => onSuccess("ok"), 0)
                }
              >
                {certificateUrl ? (
                  <img
                    src={certificateUrl}
                    alt="certificate"
                    style={{ height: "100%", maxWidth: "100%" }}
                  />
                ) : (
                  <div
                    style={{ marginTop: 8 }}
                    className="upload-profile-pic-empty"
                  >
                    <span className="upload-profile-pic-empty__plus">
                      <svg
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="11"
                          y="3"
                          width="2"
                          height="18"
                          rx="1"
                          fill="black"
                        />
                        <rect
                          x="21"
                          y="11"
                          width="2"
                          height="18"
                          rx="1"
                          transform="rotate(90 21 11)"
                          fill="black"
                        />
                      </svg>
                    </span>
                  </div>
                )}
              </Upload>
            </Form.Item>

            <Form.Item
              name="license"
              label="Лицензия"
              className="non-floating-form-item"
              valuePropName="license"
            >
              <Upload
                name="license"
                listType="picture-card"
                className="license-uploader"
                showUploadList={false}
                beforeUpload={beforeUpload}
                onChange={handleLicenseChange}
                customRequest={({ onSuccess }) =>
                  setTimeout(() => onSuccess("ok"), 0)
                }
              >
                {licenseUrl ? (
                  <img
                    src={licenseUrl}
                    alt="license"
                    style={{ height: "100%", maxWidth: "100%" }}
                  />
                ) : (
                  <div
                    style={{ marginTop: 8 }}
                    className="upload-profile-pic-empty"
                  >
                    <span className="upload-profile-pic-empty__plus">
                      <svg
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="11"
                          y="3"
                          width="2"
                          height="18"
                          rx="1"
                          fill="black"
                        />
                        <rect
                          x="21"
                          y="11"
                          width="2"
                          height="18"
                          rx="1"
                          transform="rotate(90 21 11)"
                          fill="black"
                        />
                      </svg>
                    </span>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </div>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Сохранить
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default CompanyProfile;
